// Slice H34b: Accept-invite landing page.
//
// Mounts when the URL has `?accept_invite=<token>`. Self-contained: no
// dependency on the rest of the ChatFort shell except the auth token
// in localStorage (cf_auth_token) and an optional setAuthToken handed
// down from app.jsx so the page can write the JWT after signup-via-
// invite without having to reload.
//
// Lookup flow:
//   1. GET /api/projects/invites/lookup?token=<T>  -- reveals project
//      name + inviter + role + email_already_registered, no auth.
//   2. Based on auth state + email match + email_already_registered,
//      render one of four paths: accept, signup, wrong-account, login.
//   3. On success, clear ?accept_invite from the URL and navigate to
//      #projects/<id>.
//
// Self-contained CSS to avoid theme drift on the pre-auth path (other
// pages can hide this user before they've ever clicked into the app).

import { useEffect, useState } from "react";

function tok() { try { return localStorage.getItem("cf_auth_token"); } catch { return ""; } }

async function api(path, opts = {}) {
  const headers = { ...(opts.headers || {}), "Authorization": "Bearer " + (tok() || "") };
  if (opts.body && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  const r = await fetch(path, { ...opts, headers });
  let body = null;
  try { body = await r.json(); } catch { body = { error: "http_" + r.status }; }
  if (!r.ok) {
    const e = new Error(body.reason || body.error || ("http_" + r.status));
    e.status = r.status; e.body = body;
    throw e;
  }
  return body;
}

function decodeJwtPayload(t) {
  try {
    const parts = t.split(".");
    if (parts.length !== 3) return null;
    const b = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b + "===".slice(0, (4 - (b.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch { return null; }
}

const COLOR = {
  bg:       "#0e0f12",
  panel:    "#16181d",
  border:   "#23272f",
  border2:  "#2f343d",
  text:     "#e7e9ee",
  text2:    "#aab0bc",
  text3:    "#6b7280",
  accent:   "#6366f1",
  accentFg: "#ffffff",
  err:      "#ef4444",
};

export default function AcceptInvitePage({ token, onDone, onAuthChanged }) {
  const [loading, setLoading] = useState(true);
  const [lookup, setLookup] = useState(null);  // {email, role, project_name, invited_by_email, expires_at, email_already_registered}
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Decode current JWT (if any) so we know whose account is logged in.
  const authToken = tok();
  const authPayload = authToken ? decodeJwtPayload(authToken) : null;
  const authEmail = (authPayload && authPayload.email || "").toLowerCase();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetch("/api/projects/invites/lookup?token=" + encodeURIComponent(token)).then(r => r.json().then(b => { if (!r.ok) { const e = new Error(b.error || ("http_" + r.status)); e.status = r.status; e.body = b; throw e; } return b; }));
        if (cancelled) return;
        setLookup(d);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e.body?.error || e.message || "lookup_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const clearUrlAndGoto = (projectId) => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("accept_invite");
      u.hash = projectId ? "#projects/" + encodeURIComponent(projectId) : u.hash;
      window.history.replaceState(null, "", u.toString());
    } catch {}
    onDone && onDone(projectId);
  };

  const acceptAuthed = async () => {
    setSubmitting(true);
    try {
      const r = await api("/api/projects/invites/accept", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      clearUrlAndGoto(r.project_id);
    } catch (e) {
      setError(e.body?.error || e.message || "accept_failed");
    } finally { setSubmitting(false); }
  };

  const signupAndAccept = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) { setError("name_required"); return; }
    if (password.length < 8) { setError("password_too_short"); return; }
    setSubmitting(true);
    try {
      const r = await api("/api/auth/signup-via-invite", {
        method: "POST",
        body: JSON.stringify({ token, name: name.trim(), password }),
      });
      try { localStorage.setItem("cf_auth_token", r.token); } catch {}
      if (onAuthChanged) onAuthChanged(r.token, r.user);
      clearUrlAndGoto(r.project_id);
    } catch (e) {
      setError(e.body?.error || e.message || "signup_failed");
    } finally { setSubmitting(false); }
  };

  const stashTokenAndGoLogin = () => {
    try { sessionStorage.setItem("cf_pending_invite_token", token); } catch {}
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("accept_invite");
      u.hash = "";
      window.history.replaceState(null, "", u.toString());
    } catch {}
    onDone && onDone(null);
  };

  // ── Layout: centered card on a tinted backdrop ───────────────────────
  const wrap = (children) => (
    <div className="cf-modal-backdrop" style={{ position: "fixed", inset: 0, background: COLOR.bg, color: COLOR.text, fontFamily: "Outfit, system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 1000 }}>
      <div className="cf-modal-panel" style={{ width: "100%", maxWidth: 440, background: COLOR.panel, border: `1px solid ${COLOR.border2}`, borderRadius: 14, padding: 28, boxShadow: "0 24px 60px rgba(0,0,0,0.4)" }}>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return wrap(
      <div style={{ textAlign: "center", color: COLOR.text3, fontSize: 13 }}>Looking up your invite…</div>
    );
  }

  if (error && !lookup) {
    // Hard errors -- invite not found, expired, already used.
    const friendly = {
      invite_not_found: "This invite link is invalid. Ask your inviter to send a new one.",
      invite_already_used: "This invite has already been used. Log in to your account to access the project.",
      invite_expired: "This invite has expired. Ask your inviter to send a new one.",
      bad_token: "This invite link is malformed.",
    }[error] || error;
    return wrap(
      <>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Invite unavailable</div>
        <div style={{ color: COLOR.text2, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>{friendly}</div>
        <button onClick={() => clearUrlAndGoto(null)}
          style={{ width: "100%", padding: "10px 14px", border: "none", borderRadius: 8, background: COLOR.accent, color: COLOR.accentFg, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Continue to ChatFortAI
        </button>
      </>
    );
  }

  const inv = lookup;
  const expired = inv && new Date(inv.expires_at).getTime() < Date.now();
  if (expired) {
    return wrap(
      <>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Invite expired</div>
        <div style={{ color: COLOR.text2, fontSize: 13, marginBottom: 18 }}>Ask {inv.invited_by_email} to send you a new one.</div>
        <button onClick={() => clearUrlAndGoto(null)}
          style={{ width: "100%", padding: "10px 14px", border: "none", borderRadius: 8, background: COLOR.accent, color: COLOR.accentFg, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Continue
        </button>
      </>
    );
  }

  // ── Header (shared across the auth-state branches) ───────────────────
  const header = (
    <>
      <div style={{ fontSize: 11, color: COLOR.text3, fontFamily: "monospace", letterSpacing: 0.5, marginBottom: 6, textTransform: "uppercase" }}>
        Project invite
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, lineHeight: 1.2 }}>
        {inv.project_name}
      </div>
      <div style={{ color: COLOR.text2, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>
        <b style={{ color: COLOR.text }}>{inv.invited_by_email}</b> invited <b style={{ color: COLOR.text }}>{inv.email}</b> to collaborate as <b style={{ color: COLOR.text }}>{inv.role}</b>.
      </div>
    </>
  );

  // ── Path: authed, emails match → one-click accept ────────────────────
  if (authToken && authEmail === inv.email.toLowerCase()) {
    return wrap(
      <>
        {header}
        {error && (
          <div style={{ background: COLOR.err + "22", border: `1px solid ${COLOR.err}55`, color: COLOR.err, fontSize: 12, padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
            {error === "org_mismatch"
              ? "Your account is in a different organization than this project. Cross-org collaboration isn't supported yet."
              : error}
          </div>
        )}
        <button onClick={acceptAuthed} disabled={submitting}
          style={{ width: "100%", padding: "12px 16px", border: "none", borderRadius: 8, background: COLOR.accent, color: COLOR.accentFg, fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", fontFamily: "inherit", opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "Accepting…" : "Accept invite"}
        </button>
      </>
    );
  }

  // ── Path: authed, wrong account ──────────────────────────────────────
  if (authToken && authEmail && authEmail !== inv.email.toLowerCase()) {
    return wrap(
      <>
        {header}
        <div style={{ background: COLOR.err + "22", border: `1px solid ${COLOR.err}55`, color: COLOR.err, fontSize: 12, padding: "8px 12px", borderRadius: 8, marginBottom: 12 }}>
          You're signed in as <b>{authEmail}</b> but this invite is for <b>{inv.email}</b>.
          Sign out and follow the link again from your email.
        </div>
        <button onClick={() => clearUrlAndGoto(null)}
          style={{ width: "100%", padding: "10px 14px", border: `1px solid ${COLOR.border2}`, borderRadius: 8, background: "transparent", color: COLOR.text2, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          Dismiss
        </button>
      </>
    );
  }

  // ── Path: unauthed, account already exists → ask them to log in ──────
  if (inv.email_already_registered) {
    return wrap(
      <>
        {header}
        <div style={{ color: COLOR.text2, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
          An account with this email already exists. <b>Log in</b> first, then revisit this invite link from your email.
        </div>
        <button onClick={stashTokenAndGoLogin}
          style={{ width: "100%", padding: "12px 16px", border: "none", borderRadius: 8, background: COLOR.accent, color: COLOR.accentFg, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Go to login
        </button>
      </>
    );
  }

  // ── Path: unauthed, no existing account → signup-via-invite ──────────
  return wrap(
    <>
      {header}
      <form onSubmit={signupAndAccept} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: COLOR.text3, marginBottom: 4, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 0.5 }}>Email</div>
          <input value={inv.email} disabled
            style={{ width: "100%", boxSizing: "border-box", background: COLOR.bg, border: `1px solid ${COLOR.border2}`, color: COLOR.text3, padding: "10px 12px", borderRadius: 7, fontFamily: "monospace", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: COLOR.text3, marginBottom: 4, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 0.5 }}>Your name</div>
          <input value={name} onChange={e => setName(e.target.value)} autoFocus
            placeholder="Jane Doe"
            style={{ width: "100%", boxSizing: "border-box", background: COLOR.bg, border: `1px solid ${COLOR.border2}`, color: COLOR.text, padding: "10px 12px", borderRadius: 7, fontFamily: "inherit", fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: COLOR.text3, marginBottom: 4, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 0.5 }}>Password</div>
          <input value={password} onChange={e => setPassword(e.target.value)} type="password"
            placeholder="At least 8 characters"
            style={{ width: "100%", boxSizing: "border-box", background: COLOR.bg, border: `1px solid ${COLOR.border2}`, color: COLOR.text, padding: "10px 12px", borderRadius: 7, fontFamily: "inherit", fontSize: 13 }} />
        </div>
        {error && (
          <div style={{ background: COLOR.err + "22", border: `1px solid ${COLOR.err}55`, color: COLOR.err, fontSize: 12, padding: "8px 12px", borderRadius: 8 }}>
            {{
              name_required: "Please enter your name.",
              password_too_short: "Password must be at least 8 characters.",
              user_exists: "An account with this email already exists. Log in and revisit the invite link instead.",
            }[error] || error}
          </div>
        )}
        <button type="submit" disabled={submitting || !name.trim() || password.length < 8}
          style={{ marginTop: 4, padding: "12px 16px", border: "none", borderRadius: 8, background: COLOR.accent, color: COLOR.accentFg, fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", fontFamily: "inherit", opacity: (submitting || !name.trim() || password.length < 8) ? 0.5 : 1 }}>
          {submitting ? "Creating account…" : "Sign up & join"}
        </button>
      </form>
    </>
  );
}
